import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Send, ImageIcon, X, AlertCircle, Brain, TrendingUp, Compass, PlusCircle, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { LifeContext, JournalEntry, ChatMessage, Decision, Pattern, ChatOpeningContext, AIPersonalityKey } from '../types';
import { getContextAwareResponse, mapDecision, calculateContextQuality, generateAgentGreeting, initializeChatContext, ChatSessionContext, UserFeedbackContext } from '../services/openaiService';
import { UI_STRINGS } from '../constants/translations';
import ChatCard from '../components/ChatCard';
import ChatWelcome from '../components/ChatWelcome';
import Toast from '../components/Toast';
import { trackPageView, trackAskSubmitted, trackDecisionRecorded } from '../src/lib/analytics';
import { useFeatureDiscovery } from '../contexts/FeatureDiscoveryContext';
import FeatureDiscovery from '../components/FeatureDiscovery';
import { PersonalityHeaderButton, PersonalitySelectorModal } from '../components/PersonalitySelector';
import { DEFAULT_PERSONALITY_KEY } from '../shared/aiPersonalities';

const normalizeText = (text: string): string => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

const detectSignificantContent = (text: string): { isSignificant: boolean; type: 'emotion' | 'decision' | 'reflection' | 'goal' | 'value' | null } => {
  const normalizedText = normalizeText(text);
  
  // Value indicators - detecting core beliefs and principles
  const valueIndicators = [
    // Italian - values, principles, beliefs
    'per me e importante', 'per me è importante', 'credo in', 'il mio principio', 'i miei valori', 'non posso tradire', 'devo essere fedele', 'la famiglia viene prima', 'l\'onesta e fondamentale', 'non sopporto', 'non tollero', 'e inaccettabile', 'conta davvero', 'e sacro', 'e sacra', 'non rinuncerei mai', 'non farei mai', 'sono fedele a', 'rispetto per', 'integrita', 'autenticita', 'lealta', 'liberta', 'giustizia', 'verita', 'onore', 'dignita', 'compromesso che non farei', 'guida le mie scelte', 'la cosa piu importante', 'quello che conta', 'credo fermamente', 'sono convinto', 'sono convinta', 'il mio credo', 'i miei principi', 'non mi piego', 'resto fedele', 'e fondamentale per me', 'non transigo', 'e imprescindibile', 'fa parte di me', 'definisce chi sono', 'e nella mia natura',
    // English - values, principles, beliefs
    'for me its important', 'for me it\'s important', 'i believe in', 'my principle', 'my values', 'i cant betray', 'i can\'t betray', 'i must stay true', 'family comes first', 'honesty is fundamental', 'i cant stand', 'i can\'t stand', 'i dont tolerate', 'i don\'t tolerate', 'its unacceptable', 'it\'s unacceptable', 'really matters', 'is sacred', 'i would never give up', 'i would never do', 'im loyal to', 'i\'m loyal to', 'respect for', 'integrity', 'authenticity', 'loyalty', 'freedom', 'justice', 'truth', 'honor', 'dignity', 'compromise i wouldnt make', 'guides my choices', 'the most important thing', 'what really matters', 'i firmly believe', 'im convinced', 'i\'m convinced', 'my belief', 'my principles', 'i dont bend', 'i stay true', 'is fundamental to me', 'i dont compromise', 'is essential', 'is part of me', 'defines who i am', 'its in my nature',
    // Spanish - values, principles, beliefs
    'para mi es importante', 'creo en', 'mi principio', 'mis valores', 'no puedo traicionar', 'debo ser fiel', 'la familia viene primero', 'la honestidad es fundamental', 'no soporto', 'no tolero', 'es inaceptable', 'realmente importa', 'es sagrado', 'es sagrada', 'nunca renunciaria', 'nunca haria', 'soy leal a', 'respeto por', 'integridad', 'autenticidad', 'lealtad', 'libertad', 'justicia', 'verdad', 'honor', 'dignidad', 'compromiso que no haria', 'guia mis decisiones', 'lo mas importante', 'lo que realmente importa', 'creo firmemente', 'estoy convencido', 'estoy convencida', 'mi creencia', 'mis principios', 'no me doblo', 'me mantengo fiel', 'es fundamental para mi', 'no transijo', 'es esencial', 'es parte de mi', 'define quien soy', 'esta en mi naturaleza',
    // German - values, principles, beliefs
    'fur mich ist wichtig', 'für mich ist wichtig', 'ich glaube an', 'mein prinzip', 'meine werte', 'ich kann nicht verraten', 'ich muss treu bleiben', 'familie kommt zuerst', 'ehrlichkeit ist grundlegend', 'ich kann nicht ausstehen', 'ich toleriere nicht', 'ist inakzeptabel', 'wirklich wichtig', 'ist heilig', 'wurde ich nie aufgeben', 'wurde ich nie tun', 'ich bin loyal zu', 'respekt fur', 'integritat', 'authentizitat', 'loyalitat', 'freiheit', 'gerechtigkeit', 'wahrheit', 'ehre', 'wurde', 'kompromiss den ich nie machen wurde', 'leitet meine entscheidungen', 'das wichtigste', 'was wirklich zahlt', 'ich glaube fest', 'ich bin uberzeugt', 'mein glaube', 'meine prinzipien', 'ich beuge mich nicht', 'ich bleibe treu', 'ist fundamental fur mich', 'ich mache keine kompromisse', 'ist wesentlich', 'ist teil von mir', 'definiert wer ich bin', 'liegt in meiner natur',
    // French - values, principles, beliefs
    'pour moi c\'est important', 'je crois en', 'mon principe', 'mes valeurs', 'je ne peux pas trahir', 'je dois rester fidele', 'la famille passe d\'abord', 'l\'honnetete est fondamentale', 'je ne supporte pas', 'je ne tolere pas', 'c\'est inacceptable', 'compte vraiment', 'est sacre', 'est sacree', 'je ne renoncerais jamais', 'je ne ferais jamais', 'je suis loyal a', 'respect pour', 'integrite', 'authenticite', 'loyaute', 'liberte', 'justice', 'verite', 'honneur', 'dignite', 'compromis que je ne ferais pas', 'guide mes choix', 'le plus important', 'ce qui compte vraiment', 'je crois fermement', 'je suis convaincu', 'je suis convaincue', 'ma croyance', 'mes principes', 'je ne plie pas', 'je reste fidele', 'est fondamental pour moi', 'je ne transige pas', 'est essentiel', 'fait partie de moi', 'definit qui je suis', 'c\'est dans ma nature'
  ];
  
  const emotionIndicators = [
    // Italian - emotions, feelings, states
    'mi sento', 'mi sono sentito', 'mi sono sentita', 'provo', 'ho provato', 'sono felice', 'sono triste', 'sono stanco', 'sono stanca', 'sono confuso', 'sono confusa', 'mi preoccupa', 'ho paura', 'mi fa male', 'mi rende', 'mi ha reso', 'mi emoziona', 'sono arrabbiato', 'sono arrabbiata', 'sono frustrato', 'sono frustrata', 'sono ansioso', 'sono ansiosa', 'sono nervoso', 'sono nervosa', 'sono deluso', 'sono delusa', 'sono sopraffatto', 'sono sopraffatta', 'mi sento perso', 'mi sento persa', 'sono in ansia', 'sono preoccupato', 'sono preoccupata', 'mi sento solo', 'mi sento sola', 'sono grato', 'sono grata', 'sono entusiasta', 'sono eccitato', 'sono eccitata', 'sono sereno', 'sono serena', 'sono tranquillo', 'sono tranquilla', 'mi sento bene', 'mi sento male', 'sono stressato', 'sono stressata', 'mi sento giù', 'sono depresso', 'sono depressa', 'mi sento vuoto', 'mi sento vuota', 'sono esausto', 'sono esausta', 'mi sento in pace', 'sono orgoglioso', 'sono orgogliosa', 'mi vergogno', 'sono geloso', 'sono gelosa', 'sono invidioso', 'sono invidiosa', 'mi sento libero', 'mi sento libera', 'mi sento bloccato', 'mi sento bloccata', 'mi sento ispirato', 'mi senta ispirata', 'sono motivato', 'sono motivata', 'mi sento amato', 'mi sento amata', 'mi sento apprezzato', 'mi sento apprezzata', 'mi fa stare', 'sto soffrendo', 'soffro', 'piango', 'ho pianto', 'rido', 'ho riso', 'amo', 'odio', 'detesto', 'adoro',
    // English - comprehensive emotional vocabulary
    'i feel', 'i felt', 'i am feeling', 'im feeling', 'i am happy', 'im happy', 'i am sad', 'im sad', 'i am tired', 'im tired', 'i am confused', 'im confused', 'im worried', 'i am worried', 'im afraid', 'i am afraid', 'it hurts', 'makes me feel', 'i am angry', 'im angry', 'i am frustrated', 'im frustrated', 'i am anxious', 'im anxious', 'i am nervous', 'im nervous', 'i am disappointed', 'im disappointed', 'i am overwhelmed', 'im overwhelmed', 'i feel lost', 'i am stressed', 'im stressed', 'i feel down', 'i am depressed', 'im depressed', 'i feel empty', 'i am exhausted', 'im exhausted', 'i am grateful', 'im grateful', 'i am excited', 'im excited', 'i am proud', 'im proud', 'i feel peaceful', 'i am calm', 'im calm', 'i feel good', 'i feel bad', 'i am lonely', 'im lonely', 'i feel alone', 'i am jealous', 'im jealous', 'i am motivated', 'im motivated', 'i feel inspired', 'i feel loved', 'i feel appreciated', 'i am suffering', 'im suffering', 'i love', 'i hate', 'i adore', 'i miss', 'i regret', 'i am scared', 'im scared', 'i am terrified', 'im terrified', 'i am hopeful', 'im hopeful', 'i feel hopeless', 'i am relieved', 'im relieved', 'i feel guilty', 'i am ashamed', 'im ashamed', 'i feel stuck', 'i feel free', 'i feel trapped', 'i am content', 'im content', 'i feel blessed', 'i am heartbroken', 'im heartbroken', 'i feel betrayed', 'i am hurt', 'im hurt', 'feeling', 'emotions', 'emotional',
    // Spanish - comprehensive
    'me siento', 'me senti', 'estoy feliz', 'estoy triste', 'estoy cansado', 'estoy cansada', 'estoy confundido', 'estoy confundida', 'me preocupa', 'tengo miedo', 'me duele', 'me hace sentir', 'siento que', 'estoy enojado', 'estoy enojada', 'estoy frustrado', 'estoy frustrada', 'estoy ansioso', 'estoy ansiosa', 'estoy nervioso', 'estoy nerviosa', 'estoy decepcionado', 'estoy decepcionada', 'me siento abrumado', 'me siento abrumada', 'me siento perdido', 'me siento perdida', 'estoy estresado', 'estoy estresada', 'me siento deprimido', 'me siento deprimida', 'estoy agotado', 'estoy agotada', 'estoy agradecido', 'estoy agradecida', 'estoy emocionado', 'estoy emocionada', 'estoy orgulloso', 'estoy orgullosa', 'me siento solo', 'me siento sola', 'estoy celoso', 'estoy celosa', 'estoy motivado', 'estoy motivada', 'me siento inspirado', 'me siento inspirada', 'me siento amado', 'me siento amada', 'estoy sufriendo', 'amo', 'odio', 'adoro', 'extrano', 'me arrepiento', 'estoy asustado', 'estoy asustada', 'tengo esperanza', 'me siento culpable', 'me averguenzo', 'me siento atrapado', 'me siento atrapada', 'me siento libre', 'estoy contento', 'estoy contenta', 'me siento bendecido', 'me siento bendecida', 'estoy herido', 'estoy herida', 'me siento traicionado', 'me siento traicionada',
    // German - comprehensive
    'ich fuhle', 'ich fuhlte', 'ich bin glucklich', 'ich bin traurig', 'ich bin mude', 'ich bin verwirrt', 'ich mache mir sorgen', 'ich habe angst', 'es tut weh', 'macht mich', 'ich weiss', 'ich bin wutend', 'ich bin frustriert', 'ich bin angstlich', 'ich bin nervos', 'ich bin enttauscht', 'ich bin uberfordert', 'ich fuhle mich verloren', 'ich bin gestresst', 'ich bin deprimiert', 'ich bin erschopft', 'ich bin dankbar', 'ich bin aufgeregt', 'ich bin stolz', 'ich fuhle mich einsam', 'ich bin eifersucht', 'ich bin motiviert', 'ich fuhle mich inspiriert', 'ich fuhle mich geliebt', 'ich leide', 'ich liebe', 'ich hasse', 'ich vermisse', 'ich bereue', 'ich bin erschrocken', 'ich habe hoffnung', 'ich fuhle mich schuldig', 'ich schame mich', 'ich fuhle mich gefangen', 'ich fuhle mich frei', 'ich bin zufrieden', 'ich fuhle mich gesegnet', 'ich bin verletzt', 'ich fuhle mich verraten', 'mir geht es', 'es geht mir',
    // French - comprehensive
    'je me sens', 'je me suis senti', 'je me suis sentie', 'je suis heureux', 'je suis heureuse', 'je suis triste', 'je suis fatigue', 'je suis fatiguee', 'je suis confus', 'je suis confuse', 'je m\'inquiete', 'j\'ai peur', 'ca me fait mal', 'ca me rend', 'je suis en colere', 'je suis frustre', 'je suis frustree', 'je suis anxieux', 'je suis anxieuse', 'je suis nerveux', 'je suis nerveuse', 'je suis decu', 'je suis decue', 'je me sens submerge', 'je me sens submergee', 'je me sens perdu', 'je me sens perdue', 'je suis stresse', 'je suis stressee', 'je suis deprime', 'je suis deprimee', 'je suis epuise', 'je suis epuisee', 'je suis reconnaissant', 'je suis reconnaissante', 'je suis excite', 'je suis excitee', 'je suis fier', 'je suis fiere', 'je me sens seul', 'je me sens seule', 'je suis jaloux', 'je suis jalouse', 'je suis motive', 'je suis motivee', 'je me sens inspire', 'je me sens inspiree', 'je me sens aime', 'je me sens aimee', 'je souffre', 'j\'aime', 'je deteste', 'j\'adore', 'je regrette', 'j\'ai honte', 'je me sens coupable', 'je me sens piege', 'je me sens piegee', 'je me sens libre', 'je suis content', 'je suis contente', 'je me sens beni', 'je me sens benie', 'je suis blesse', 'je suis blessee', 'je me sens trahi', 'je me sens trahie'
  ];
  
  const decisionIndicators = [
    // Italian - decisions, choices, dilemmas
    'ho deciso', 'devo decidere', 'sto pensando di', 'voglio', 'vorrei', 'potrei', 'dovrei', 'non so se', 'sono indeciso', 'sono indecisa', 'sto valutando', 'ho scelto', 'preferisco', 'devo scegliere', 'mi chiedo se', 'forse dovrei', 'penso di', 'credo che', 'mi domando', 'sto considerando', 'valuto', 'opzione', 'alternativa', 'bivio', 'scelta difficile', 'decisione importante', 'non riesco a decidere', 'da una parte', 'dall\'altra', 'pro e contro', 'vantaggi', 'svantaggi', 'cosa faccio', 'che faccio', 'meglio se', 'conviene', 'vale la pena', 'rischio di', 'opportunita', 'cambiare lavoro', 'trasferirmi', 'lasciare', 'iniziare', 'smettere', 'accettare', 'rifiutare', 'sposarmi', 'separarmi', 'divorziare', 'avere figli', 'comprare casa', 'vendere', 'investire', 'studiare', 'laurearmi', 'cambiare citta', 'andare via', 'restare', 'partire', 'tornare',
    // English - comprehensive decision vocabulary
    'i decided', 'i have to decide', 'im thinking about', 'i want to', 'i would like', 'i could', 'i should', 'i dont know if', 'im undecided', 'im considering', 'i chose', 'i prefer', 'should i', 'i need to choose', 'i wonder if', 'maybe i should', 'i think i', 'i believe', 'i am wondering', 'option', 'alternative', 'crossroads', 'difficult choice', 'important decision', 'i cant decide', 'on one hand', 'on the other hand', 'pros and cons', 'advantages', 'disadvantages', 'what do i do', 'what should i', 'is it worth', 'risk', 'opportunity', 'change jobs', 'move', 'leave', 'start', 'quit', 'accept', 'reject', 'get married', 'divorce', 'have kids', 'buy a house', 'sell', 'invest', 'study', 'graduate', 'relocate', 'stay', 'go', 'return', 'weighing', 'evaluating', 'considering', 'contemplating', 'debating', 'torn between', 'dilemma', 'uncertain about', 'not sure if', 'thinking of', 'planning to', 'might', 'perhaps', 'possibly',
    // Spanish - comprehensive
    'he decidido', 'tengo que decidir', 'estoy pensando en', 'quiero', 'quisiera', 'podria', 'deberia', 'no se si', 'estoy indeciso', 'estoy indecisa', 'estoy considerando', 'elegi', 'prefiero', 'debo elegir', 'me pregunto si', 'tal vez deberia', 'pienso que', 'creo que', 'opcion', 'alternativa', 'encrucijada', 'decision dificil', 'decision importante', 'no puedo decidir', 'por un lado', 'por otro lado', 'pros y contras', 'ventajas', 'desventajas', 'que hago', 'que deberia', 'vale la pena', 'riesgo', 'oportunidad', 'cambiar de trabajo', 'mudarme', 'dejar', 'empezar', 'renunciar', 'aceptar', 'rechazar', 'casarme', 'divorciarme', 'tener hijos', 'comprar casa', 'vender', 'invertir', 'estudiar', 'graduarme', 'quedarme', 'irme', 'volver', 'dilema', 'inseguro', 'insegura',
    // German - comprehensive
    'ich habe entschieden', 'ich muss entscheiden', 'ich denke daruber nach', 'ich will', 'ich mochte', 'ich konnte', 'ich sollte', 'ich weiss nicht ob', 'ich weis nicht ob', 'ich bin unentschlossen', 'ich uberlege', 'ich habe gewahlt', 'ich bevorzuge', 'ich muss wahlen', 'ich frage mich', 'vielleicht sollte ich', 'ich denke dass', 'ich glaube', 'option', 'alternative', 'scheideweg', 'schwierige entscheidung', 'wichtige entscheidung', 'ich kann mich nicht entscheiden', 'einerseits', 'andererseits', 'vor und nachteile', 'vorteile', 'nachteile', 'was soll ich tun', 'lohnt es sich', 'risiko', 'gelegenheit', 'job wechseln', 'umziehen', 'verlassen', 'anfangen', 'aufhoren', 'annehmen', 'ablehnen', 'heiraten', 'scheiden', 'kinder haben', 'haus kaufen', 'verkaufen', 'investieren', 'studieren', 'bleiben', 'gehen', 'zuruckkehren', 'dilemma',
    // French - comprehensive
    'j\'ai decide', 'je dois decider', 'je pense a', 'je veux', 'je voudrais', 'je pourrais', 'je devrais', 'je ne sais pas si', 'je suis indecis', 'je suis indecise', 'j\'envisage', 'j\'ai choisi', 'je prefere', 'je dois choisir', 'je me demande si', 'peut etre devrais je', 'je pense que', 'je crois que', 'option', 'alternative', 'carrefour', 'choix difficile', 'decision importante', 'je ne peux pas decider', 'd\'un cote', 'de l\'autre', 'avantages et inconvenients', 'avantages', 'inconvenients', 'que dois je faire', 'ca vaut le coup', 'risque', 'opportunite', 'changer de travail', 'demenager', 'quitter', 'commencer', 'arreter', 'accepter', 'refuser', 'me marier', 'divorcer', 'avoir des enfants', 'acheter une maison', 'vendre', 'investir', 'etudier', 'rester', 'partir', 'revenir', 'dilemme', 'hesiter', 'j\'hesite'
  ];
  
  const reflectionIndicators = [
    // Italian - insights, realizations, learning
    'ho capito', 'ho realizzato', 'mi sono reso conto', 'mi sono resa conto', 'ho imparato', 'riflettendo', 'pensandoci', 'ho notato', 'mi accorgo', 'era saggio', 'mi ha insegnato', 'ora capisco', 'finalmente', 'guardando indietro', 'col senno di poi', 'mi rendo conto', 'ho scoperto', 'ho compreso', 'e stato illuminante', 'ho avuto un\'intuizione', 'insight', 'riflessione', 'pensiero', 'considerazione', 'osservazione', 'lezione', 'esperienza', 'crescita', 'maturazione', 'evoluzione', 'cambiamento', 'trasformazione', 'consapevolezza', 'autoconsapevolezza', 'introspezione', 'meditazione', 'contemplazione', 'saggezza', 'comprensione', 'chiarezza', 'lucidita', 'prospettiva', 'punto di vista', 'angolazione', 'vedo le cose', 'mi sono accorto', 'mi sono accorta', 'e chiaro che', 'e evidente', 'ho visto', 'ho notato', 'riconosco', 'ammetto', 'accetto',
    // English - comprehensive
    'i understand', 'i realized', 'ive realized', 'i learned', 'ive learned', 'reflecting on', 'thinking about it', 'i noticed', 'i realize', 'it taught me', 'looking back', 'now i understand', 'finally', 'hindsight', 'i see now', 'i discovered', 'i comprehend', 'it was enlightening', 'insight', 'reflection', 'thought', 'consideration', 'observation', 'lesson', 'experience', 'growth', 'maturation', 'evolution', 'change', 'transformation', 'awareness', 'self awareness', 'introspection', 'meditation', 'contemplation', 'wisdom', 'understanding', 'clarity', 'perspective', 'point of view', 'i see things', 'it is clear', 'it is evident', 'i recognize', 'i admit', 'i accept', 'it dawned on me', 'it hit me', 'it occurred to me', 'i came to realize', 'i have come to', 'over time', 'gradually', 'with experience', 'as i grow', 'personal growth', 'self discovery', 'epiphany', 'revelation', 'breakthrough', 'aha moment', 'lightbulb moment', 'wake up call', 'reality check',
    // Spanish - comprehensive
    'he entendido', 'me di cuenta', 'he aprendido', 'reflexionando', 'pensandolo', 'he notado', 'me doy cuenta', 'me enseno', 'mirando atras', 'ahora entiendo', 'finalmente', 'en retrospectiva', 'he descubierto', 'he comprendido', 'fue revelador', 'introspeccion', 'reflexion', 'pensamiento', 'consideracion', 'observacion', 'leccion', 'experiencia', 'crecimiento', 'maduracion', 'evolucion', 'cambio', 'transformacion', 'conciencia', 'autoconciencia', 'meditacion', 'contemplacion', 'sabiduria', 'comprension', 'claridad', 'perspectiva', 'punto de vista', 'veo las cosas', 'es claro que', 'reconozco', 'admito', 'acepto', 'epifania', 'revelacion', 'despertar',
    // German - comprehensive
    'ich verstehe', 'mir ist klar geworden', 'ich habe gelernt', 'beim nachdenken', 'ich habe bemerkt', 'mir fallt auf', 'es hat mir beigebracht', 'ruckblickend', 'jetzt verstehe ich', 'endlich', 'im nachhinein', 'ich habe entdeckt', 'ich habe begriffen', 'es war aufschlussreich', 'einsicht', 'reflexion', 'gedanke', 'uberlegung', 'beobachtung', 'lektion', 'erfahrung', 'wachstum', 'reifung', 'entwicklung', 'veranderung', 'transformation', 'bewusstsein', 'selbstbewusstsein', 'meditation', 'kontemplation', 'weisheit', 'verstandnis', 'klarheit', 'perspektive', 'standpunkt', 'ich sehe die dinge', 'es ist klar', 'ich erkenne', 'ich gebe zu', 'ich akzeptiere', 'offenbarung', 'durchbruch',
    // French - comprehensive
    'j\'ai compris', 'je me suis rendu compte', 'je me suis rendue compte', 'j\'ai appris', 'en reflechissant', 'en y pensant', 'j\'ai remarque', 'je me rends compte', 'ca m\'a appris', 'en regardant en arriere', 'maintenant je comprends', 'finalement', 'avec le recul', 'j\'ai decouvert', 'c\'etait revelateur', 'introspection', 'reflexion', 'pensee', 'consideration', 'observation', 'lecon', 'experience', 'croissance', 'maturation', 'evolution', 'changement', 'transformation', 'conscience', 'conscience de soi', 'meditation', 'contemplation', 'sagesse', 'comprehension', 'clarte', 'perspective', 'point de vue', 'je vois les choses', 'c\'est clair que', 'je reconnais', 'j\'admets', 'j\'accepte', 'revelation', 'epiphanie', 'prise de conscience'
  ];
  
  const goalIndicators = [
    // Italian - goals, achievements, progress
    'il mio obiettivo', 'voglio raggiungere', 'sto lavorando su', 'mi impegno a', 'ho fatto progressi', 'sono riuscito', 'sono riuscita', 'ce l\'ho fatta', 'ho completato', 'ho finito', 'sogno di', 'aspiro a', 'punto a', 'miro a', 'il mio sogno', 'la mia ambizione', 'la mia visione', 'il mio piano', 'il mio progetto', 'traguardo', 'risultato', 'successo', 'vittoria', 'conquista', 'realizzazione', 'compimento', 'milestone', 'step', 'passo avanti', 'avanzamento', 'miglioramento', 'crescita', 'sviluppo', 'evoluzione', 'ho raggiunto', 'ho ottenuto', 'ho conquistato', 'ho superato', 'sfida', 'challenge', 'obiettivo raggiunto', 'missione compiuta', 'fatto', 'completato', 'terminato', 'concluso', 'portato a termine', 'sto facendo progressi', 'vado avanti', 'procedo', 'avanzo', 'miglioro', 'cresco', 'evolvo', 'mi sviluppo', 'imparo', 'mi alleno', 'pratico', 'mi esercito', 'sto imparando', 'sto migliorando', 'sto crescendo',
    // English - comprehensive
    'my goal', 'i want to achieve', 'im working on', 'i commit to', 'i made progress', 'i succeeded', 'i did it', 'i completed', 'i finished', 'i accomplished', 'i dream of', 'i aspire to', 'i aim for', 'my dream', 'my ambition', 'my vision', 'my plan', 'my project', 'milestone', 'result', 'success', 'victory', 'achievement', 'accomplishment', 'fulfillment', 'step forward', 'advancement', 'improvement', 'growth', 'development', 'evolution', 'i achieved', 'i obtained', 'i conquered', 'i overcame', 'challenge', 'goal achieved', 'mission accomplished', 'done', 'completed', 'finished', 'concluded', 'i am making progress', 'moving forward', 'proceeding', 'advancing', 'improving', 'growing', 'evolving', 'developing', 'learning', 'training', 'practicing', 'exercising', 'i am learning', 'i am improving', 'i am growing', 'bucket list', 'life goal', 'dream job', 'new year resolution', 'personal best', 'breakthrough', 'level up', 'next level', 'upgrade', 'transform', 'become', 'i want to be', 'i will be', 'i am becoming',
    // Spanish - comprehensive
    'mi objetivo', 'quiero lograr', 'estoy trabajando en', 'me comprometo a', 'he progresado', 'lo logre', 'lo consegui', 'he completado', 'he terminado', 'sueno con', 'aspiro a', 'apunto a', 'mi sueno', 'mi ambicion', 'mi vision', 'mi plan', 'mi proyecto', 'hito', 'resultado', 'exito', 'victoria', 'logro', 'realizacion', 'cumplimiento', 'paso adelante', 'avance', 'mejora', 'crecimiento', 'desarrollo', 'evolucion', 'he alcanzado', 'he obtenido', 'he conquistado', 'he superado', 'desafio', 'reto', 'objetivo logrado', 'mision cumplida', 'hecho', 'completado', 'terminado', 'concluido', 'estoy progresando', 'avanzo', 'mejoro', 'crezco', 'evoluciono', 'me desarrollo', 'aprendo', 'entreno', 'practico', 'estoy aprendiendo', 'estoy mejorando', 'estoy creciendo', 'meta de vida', 'proposito',
    // German - comprehensive
    'mein ziel', 'ich will erreichen', 'ich arbeite an', 'ich verpflichte mich', 'ich habe fortschritte gemacht', 'ich habe es geschafft', 'ich habe abgeschlossen', 'ich habe fertig', 'ich traume von', 'ich strebe nach', 'ich ziele auf', 'mein traum', 'meine ambition', 'meine vision', 'mein plan', 'mein projekt', 'meilenstein', 'ergebnis', 'erfolg', 'sieg', 'leistung', 'errungenschaft', 'erfullung', 'schritt vorwarts', 'fortschritt', 'verbesserung', 'wachstum', 'entwicklung', 'ich habe erreicht', 'ich habe erhalten', 'ich habe erobert', 'ich habe uberwunden', 'herausforderung', 'ziel erreicht', 'mission erfullt', 'erledigt', 'abgeschlossen', 'beendet', 'ich mache fortschritte', 'ich komme voran', 'ich verbessere mich', 'ich wachse', 'ich entwickle mich', 'ich lerne', 'ich trainiere', 'ich ube', 'ich lerne gerade', 'ich verbessere mich gerade', 'lebensziel',
    // French - comprehensive
    'mon objectif', 'je veux atteindre', 'je travaille sur', 'je m\'engage a', 'j\'ai fait des progres', 'j\'ai reussi', 'j\'y suis arrive', 'j\'y suis arrivee', 'j\'ai termine', 'j\'ai fini', 'je reve de', 'j\'aspire a', 'je vise', 'mon reve', 'mon ambition', 'ma vision', 'mon plan', 'mon projet', 'etape importante', 'resultat', 'succes', 'victoire', 'accomplissement', 'realisation', 'pas en avant', 'avancement', 'amelioration', 'croissance', 'developpement', 'evolution', 'j\'ai atteint', 'j\'ai obtenu', 'j\'ai conquis', 'j\'ai surmonte', 'defi', 'objectif atteint', 'mission accomplie', 'fait', 'complete', 'termine', 'conclu', 'je progresse', 'j\'avance', 'je m\'ameliore', 'je grandis', 'j\'evolue', 'je me developpe', 'j\'apprends', 'je m\'entraine', 'je pratique', 'je suis en train d\'apprendre', 'je suis en train de m\'ameliorer', 'objectif de vie'
  ];

  if (valueIndicators.some(i => normalizedText.includes(i))) return { isSignificant: true, type: 'value' };
  if (emotionIndicators.some(i => normalizedText.includes(i))) return { isSignificant: true, type: 'emotion' };
  if (decisionIndicators.some(i => normalizedText.includes(i))) return { isSignificant: true, type: 'decision' };
  if (reflectionIndicators.some(i => normalizedText.includes(i))) return { isSignificant: true, type: 'reflection' };
  if (goalIndicators.some(i => normalizedText.includes(i))) return { isSignificant: true, type: 'goal' };
  
  if (text.length > 60) return { isSignificant: true, type: 'reflection' };
  
  return { isSignificant: false, type: null };
};

// Typing effect hook for streaming text appearance
const useTypingEffect = (text: string, speed: number = 15, enabled: boolean = true) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setDisplayedText(text);
      setIsComplete(true);
      return;
    }

    setDisplayedText('');
    setIsComplete(false);
    
    if (!text) return;

    let index = 0;
    const timer = setInterval(() => {
      if (index < text.length) {
        setDisplayedText(text.slice(0, index + 1));
        index++;
      } else {
        setIsComplete(true);
        clearInterval(timer);
      }
    }, speed);

    return () => clearInterval(timer);
  }, [text, speed, enabled]);

  return { displayedText, isComplete };
};

interface AskViewProps {
  context: LifeContext;
  entries: JournalEntry[];
  decisions: Decision[];
  patterns?: Pattern[];
  onNewEntry: (content: string, imageUrl?: string, mood?: string) => Promise<any>;
  setDecisions: React.Dispatch<React.SetStateAction<Decision[]>>;
  onDecisionCreated?: (decision: Decision) => void;
  handleApiError?: (error: any) => boolean;
  onNavigate?: (screen: 'pulse' | 'archive' | 'consult' | 'decisions' | 'insights') => void;
  onAddValue?: (value: string) => void;
  onAddGoal?: (goal: string) => void;
  onPersonalityChange?: (key: AIPersonalityKey) => Promise<void>;
}

// Direct action from AI (1-click execute)
interface DirectAction {
  type: 'TRACK' | 'DECIDE' | 'VALUE' | 'GOAL';
  title: string;
  reason: string;
}

// Extended ChatMessage with citations and context used
interface ExtendedChatMessage extends ChatMessage {
  citedEntries?: Array<{ id: string; date: string; snippet: string }>;
  patternsUsed?: string[];
  valuesUsed?: string[];
  goalsUsed?: string[];
  peopleUsed?: string[];
  placesUsed?: string[];
  decisionsUsed?: string[];
  emotionalMode?: 'struggle' | 'celebration' | 'neutral' | 'curiosity';
  directActions?: DirectAction[];
  responseType?: 'support' | 'coaching' | 'exploration' | 'celebration' | 'reflection';
  showSuggestions?: boolean;
}

const AskView: React.FC<AskViewProps> = ({ context, entries, decisions, patterns = [], onNewEntry, setDecisions, onDecisionCreated, handleApiError, onNavigate, onAddValue, onAddGoal, onPersonalityChange }) => {
  const t = useMemo(() => UI_STRINGS[context.language] || UI_STRINGS.English, [context.language]);
  const { state: discoveryState, getNextStepForView, completeStep } = useFeatureDiscovery();
  const currentDiscoveryStep = useMemo(() => getNextStepForView('chat'), [getNextStepForView]);

  // Session context - loaded ONCE at chat start, reused for all messages
  const [sessionContext, setSessionContext] = useState<ChatSessionContext | null>(null);
  const sessionContextLoadedRef = useRef(false);

  // Feedback context - loaded ONCE to integrate user preferences in AI responses
  const [feedbackContext, setFeedbackContext] = useState<UserFeedbackContext | null>(null);

  // AI Personality state
  const [isPersonalityModalOpen, setIsPersonalityModalOpen] = useState(false);
  const currentPersonalityKey = context.selectedPersonalityKey || DEFAULT_PERSONALITY_KEY;
  
  const saveChatMessage = useCallback(async (message: { id: string; role: string; content: string; imageUrl?: string; contextLinks?: string[] }) => {
    try {
      await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          id: message.id,
          role: message.role,
          content: message.content,
          imageUrl: message.imageUrl || null,
          contextLinks: message.contextLinks || null
        })
      });
    } catch (err) {
      console.error('Error saving chat message:', err);
    }
  }, []);

  const handleDiscoveryActivate = useCallback((step: keyof typeof discoveryState) => {
    if (step === 'consultIntro') {
      // Info step - just complete
      completeStep('consultIntro');
    } else if (step === 'chatDiscovered') {
      setTimeout(() => chatInputRef.current?.focus(), 100);
    } else if (step === 'chatTrackingDiscovered') {
      // Pre-fill with tracking suggestion and focus
      const trackingSuggestions: Record<string, string> = {
        Italian: "Oggi ho fatto qualcosa di importante che voglio ricordare",
        English: "Today I did something important I want to remember",
        Spanish: "Hoy hice algo importante que quiero recordar",
        German: "Heute habe ich etwas Wichtiges getan, das ich mir merken möchte",
        French: "Aujourd'hui j'ai fait quelque chose d'important dont je veux me souvenir"
      };
      setInputValue(trackingSuggestions[context.language] || trackingSuggestions.English);
      setTimeout(() => chatInputRef.current?.focus(), 100);
    }
  }, [completeStep, context.language]);

  const [messages, setMessages] = useState<ExtendedChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    trackPageView('/ask', 'Ask', true);
  }, []);

  // Initialize session context and feedback context ONCE when chat opens
  useEffect(() => {
    const loadSessionContext = async () => {
      if (sessionContextLoadedRef.current) return;
      sessionContextLoadedRef.current = true;

      try {
        // Load session context and feedback context in parallel
        const [cachedContext, feedbackResponse] = await Promise.all([
          initializeChatContext(context, entries, decisions, patterns),
          fetch('/api/insight-feedback/ai-context', { credentials: 'include' }).then(r => r.ok ? r.json() : null)
        ]);
        setSessionContext(cachedContext);
        if (feedbackResponse) {
          setFeedbackContext(feedbackResponse);
        }
      } catch (err) {
        console.error('Error initializing chat context:', err);
      }
    };

    loadSessionContext();
  }, []); // Empty deps - load only once on mount
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [justTracked, setJustTracked] = useState<string[]>(() => {
    const saved = localStorage.getItem('egovex_trackedActions');
    return saved ? JSON.parse(saved) : [];
  });
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [lastMessageId, setLastMessageId] = useState<string | null>(null);
  const [isUserNearBottom, setIsUserNearBottom] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1024);
  const hasMountedRef = useRef(false);
  const userHasScrolledRef = useRef(false);
  const [quickTrackedMsgs, setQuickTrackedMsgs] = useState<string[]>(() => {
    const saved = localStorage.getItem('egovex_quickTrackedMsgs');
    return saved ? JSON.parse(saved) : [];
  });
  const [headerExpanded, setHeaderExpanded] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLInputElement>(null);

  // Track desktop vs mobile for scroll behavior
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleQuickTrack = async (msgId: string, content: string, imageUrl?: string) => {
    if (quickTrackedMsgs.includes(msgId)) return;
    
    // Optimistic update - show feedback immediately
    setQuickTrackedMsgs(prev => {
      const updated = [...prev, msgId];
      localStorage.setItem('egovex_quickTrackedMsgs', JSON.stringify(updated));
      return updated;
    });
    setToastMessage(t.askPage.momentSaved);
    
    // Do the actual work in background
    try {
      await onNewEntry(content, imageUrl);
    } catch (err) {
      // Rollback on error
      setQuickTrackedMsgs(prev => {
        const updated = prev.filter(id => id !== msgId);
        localStorage.setItem('egovex_quickTrackedMsgs', JSON.stringify(updated));
        return updated;
      });
      setToastMessage(t.askPage.saveError);
    }
  };

  // Track if user is near bottom of scroll
  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setIsUserNearBottom(isNearBottom);
    }
  }, []);

  // Calculate context quality
  const contextQuality = useMemo(() =>
    calculateContextQuality(context, entries, patterns, decisions),
    [context, entries.length, patterns.length, decisions.length]
  );

  // Smart scroll: only auto-scroll after user has interacted
  // Initial AI message stays visible at top
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    
    if (scrollContainerRef.current && userHasScrolledRef.current && isUserNearBottom && !isDesktop) {
      scrollContainerRef.current.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, isTyping, isUserNearBottom, isDesktop]);

  // Track when user sends a message to enable auto-scroll
  useEffect(() => {
    const userMessages = messages.filter(m => m.role === 'user');
    if (userMessages.length > 0) {
      userHasScrolledRef.current = true;
    }
  }, [messages]);

  useEffect(() => {
    if (messages.length === 0) {
      const isFirstTime = context.isFirstTimeUser === true;
      const firstTimeSuggestions = t.askSuggestions?.firstTime || {
        tellMeAboutToday: "Tell me something that happened today",
        whatAreYouThinking: "Share what you're thinking right now",
        aDecisionToMake: "I have a decision to make"
      };
      
      const initial = isFirstTime
        ? [
            firstTimeSuggestions.tellMeAboutToday,
            firstTimeSuggestions.whatAreYouThinking,
            firstTimeSuggestions.aDecisionToMake
          ]
        : context.goals.length > 0
          ? [t.askSuggestions?.goalProgress?.replace('{goal}', context.goals[0]) || `How am I doing with "${context.goals[0]}"?`, t.askSuggestions?.analyzeValues || "Analyze my values", t.askSuggestions?.difficultDecision || "I have a difficult decision to make"]
          : [t.askSuggestions?.whatCanYouDo || "What can you do for me?", t.askSuggestions?.analyzeDay || "Analyze my day", t.askSuggestions?.helpDecide || "Help me decide"];
      setSuggestions(initial);
    }
  }, [context.goals, context.isFirstTimeUser, messages.length, t]);

  // Agent-first pattern: Check for chat context and generate agent greeting
  useEffect(() => {
    const handleAgentFirstOpening = async () => {
      const chatContextStr = sessionStorage.getItem('chatContext');
      if (chatContextStr) {
        sessionStorage.removeItem('chatContext');
        try {
          const openingContext: ChatOpeningContext = JSON.parse(chatContextStr);
          setIsTyping(true);
          setSuggestions([]);
          
          const result = await generateAgentGreeting(openingContext, context, entries, decisions, patterns, feedbackContext || undefined);
          
          const agentMsg: ExtendedChatMessage = {
            id: Date.now().toString(),
            role: 'model',
            content: result.greeting,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            citedEntries: result.citedEntries,
            patternsUsed: result.patternsUsed
          };
          
          setMessages([agentMsg]);
          setSuggestions(result.suggestions.length > 0 ? result.suggestions : []);
          setIsTyping(false);
        } catch (err) {
          console.error('Error generating agent greeting:', err);
          setIsTyping(false);
        }
      }
    };
    
    handleAgentFirstOpening();
  }, []);

  const parseResponse = (text: string = "") => {
    const actionRegex = /\[ACTION:(TRACK|DECIDE|VALUE|GOAL)\]\s*"(.*?)"/gi;
    const suggestionRegex = /\[SUGGESTION\]\s*(.*?)(?=\[|$)/gi;
    
    const actions: {type: string, title: string}[] = [];
    let match;
    while ((match = actionRegex.exec(text)) !== null) {
      if (match[1] && match[2]) actions.push({ type: match[1], title: match[2] });
    }

    const newSuggestions: string[] = [];
    let sMatch;
    while ((sMatch = suggestionRegex.exec(text)) !== null) {
      if (sMatch[1]) newSuggestions.push(sMatch[1].trim());
    }

    const cleanContent = text
      .replace(actionRegex, '')
      .replace(suggestionRegex, '')
      .replace(/\[ACTION:.*?\]/gi, '')
      .trim();

    // If we have valid content, return it. Otherwise return the original text if available
    // This prevents showing generic fallback messages when the AI response failed
    const finalContent = cleanContent || text?.trim() || "";
    return { cleanContent: finalContent, actions, newSuggestions };
  };

  // Build conversation history for context
  const getConversationHistory = () => {
    return messages.map(m => ({
      role: m.role,
      content: m.content
    }));
  };

  // Handle suggestion click - agent responds directly without showing user message
  const handleSuggestionClick = async (suggestionText: string) => {
    setError(null);
    setIsTyping(true);
    setSuggestions([]);

    // Check if this is a decision-related suggestion that should use special context
    const isDecisionSuggestion = 
      suggestionText.toLowerCase().includes('decisione') ||
      suggestionText.toLowerCase().includes('decision') ||
      suggestionText.toLowerCase().includes('decidere') ||
      suggestionText.toLowerCase().includes('decide') ||
      suggestionText.toLowerCase().includes('scelta') ||
      suggestionText.toLowerCase().includes('choice') ||
      suggestionText === t.askSuggestions?.difficultDecision ||
      suggestionText === t.askSuggestions?.helpDecide;

    // If it's a decision suggestion at the start of conversation, use generateAgentGreeting with decision_help context
    if (isDecisionSuggestion && messages.length === 0) {
      try {
        const openingContext: ChatOpeningContext = {
          type: 'decision_help',
          data: {}
        };
        
        const result = await generateAgentGreeting(openingContext, context, entries, decisions, patterns, feedbackContext || undefined);
        
        const agentMsg: ExtendedChatMessage = {
          id: Date.now().toString(),
          role: 'model',
          content: result.greeting,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          citedEntries: result.citedEntries,
          patternsUsed: result.patternsUsed
        };
        
        setMessages([agentMsg]);
        saveChatMessage({ id: agentMsg.id, role: 'model', content: agentMsg.content });
        setSuggestions(result.suggestions.length > 0 ? result.suggestions : []);
        setIsTyping(false);
        return;
      } catch (err) {
        console.error('Error generating decision help greeting:', err);
        setIsTyping(false);
        return;
      }
    }

    try {
      // Build conversation history from current messages (WITHOUT adding the suggestion as user message)
      const conversationHistory = messages.map(m => ({
        role: m.role,
        content: m.content
      }));

      // Frame the suggestion as an agent-driven exploration topic, not a user question
      // This tells the AI to proactively explore this topic WITH the user
      const explorationPrompts: Record<string, string> = {
        'Italian': `[EXPLORATION_TOPIC] L'utente ha scelto di esplorare: "${suggestionText}". 
Tu INIZI la riflessione su questo tema. NON è una domanda dell'utente a te - è un tema che TU esplori CON loro.
Usa il contesto disponibile per offrire una prospettiva esistenziale significativa su questo tema.
Connetti il tema ai valori, pattern, obiettivi dell'utente. Offri insight che stimolino la riflessione.
Termina con una domanda aperta breve (max 8 parole) che approfondisca il tema.`,
        'English': `[EXPLORATION_TOPIC] The user chose to explore: "${suggestionText}". 
YOU initiate the reflection on this theme. This is NOT a question from the user to you - it's a topic YOU explore WITH them.
Use the available context to offer a meaningful existential perspective on this theme.
Connect it to their values, patterns, and goals. Offer insights that spark reflection.
End with a short open question (max 8 words) that deepens the theme.`,
        'Spanish': `[EXPLORATION_TOPIC] El usuario eligió explorar: "${suggestionText}". 
TÚ inicias la reflexión sobre este tema. NO es una pregunta del usuario hacia ti - es un tema que TÚ exploras CON ellos.
Usa el contexto disponible para ofrecer una perspectiva existencial significativa sobre este tema.
Conéctalo con sus valores, patrones y objetivos. Ofrece insights que estimulen la reflexión.
Termina con una pregunta abierta breve (máx 8 palabras) que profundice el tema.`,
        'German': `[EXPLORATION_TOPIC] Der Benutzer möchte erkunden: "${suggestionText}". 
DU beginnst die Reflexion zu diesem Thema. Dies ist KEINE Frage des Benutzers an dich - es ist ein Thema, das DU gemeinsam MIT ihm erkundest.
Nutze den verfügbaren Kontext, um eine bedeutsame existenzielle Perspektive zu diesem Thema anzubieten.
Verbinde es mit seinen Werten, Mustern und Zielen. Biete Einsichten, die zur Selbstreflexion anregen.
Beende mit einer kurzen offenen Frage (max 8 Wörter), die das Thema vertieft.`,
        'French': `[EXPLORATION_TOPIC] L'utilisateur a choisi d'explorer: "${suggestionText}". 
TU inities la réflexion sur ce thème. Ce n'est PAS une question de l'utilisateur vers toi - c'est un thème que TU explores AVEC lui.
Utilise le contexte disponible pour offrir une perspective existentielle significative sur ce thème.
Connecte-le à ses valeurs, schémas et objectifs. Offre des insights qui stimulent la réflexion.
Termine par une question ouverte courte (max 8 mots) qui approfondit le thème.`
      };
      const userLang = context?.language || 'English';
      const agentExplorationPrompt = explorationPrompts[userLang] || explorationPrompts['English'];

      // Use context-aware response with CACHED session context
      const result = await getContextAwareResponse(
        agentExplorationPrompt,
        context,
        entries,
        decisions,
        patterns,
        conversationHistory,
        sessionContext, // Pass cached context for efficiency
        feedbackContext || undefined
      );
      
      const { cleanContent, actions: parsedActions, newSuggestions } = parseResponse(result.response);
      const directActions = (result as any).directActions || [];
      const finalActions = directActions.length > 0 ? directActions : parsedActions.map((a: any) => ({ ...a, reason: '' }));

      const msgId = (Date.now() + 1).toString();
      const suggEntities = (result as any).extractedEntities || { people: [], places: [], decisions: [] };
      const botMsg: ExtendedChatMessage = {
        id: msgId,
        role: 'model',
        content: cleanContent,
        contextLinks: finalActions.map((a: any) => `${a.type}|${a.title}`),
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        citedEntries: result.citedEntries,
        patternsUsed: result.patternsUsed,
        valuesUsed: result.valuesUsed,
        goalsUsed: result.goalsUsed,
        peopleUsed: suggEntities.people || [],
        placesUsed: suggEntities.places || [],
        decisionsUsed: suggEntities.decisions || [],
        emotionalMode: result.emotionalMode,
        directActions: finalActions,
        responseType: (result as any).responseType || 'support',
        showSuggestions: (result as any).showSuggestions !== false
      };

      setMessages(prev => [...prev, botMsg]);
      setLastMessageId(msgId);
      
      saveChatMessage({ id: botMsg.id, role: 'model', content: botMsg.content, contextLinks: botMsg.contextLinks });
      
      // Set suggestions from AI response for conversation continuation
      if (result.suggestions && result.suggestions.length > 0) {
        setSuggestions(result.suggestions);
      } else if (newSuggestions.length > 0) {
        setSuggestions(newSuggestions);
      } else {
        const as = t.askSuggestions;
        const fallbackSuggestions = result.emotionalMode === 'struggle'
          ? [as.whatWouldHelp, as.tellMeMore, as.whatBlocksYou]
          : result.emotionalMode === 'celebration'
            ? [as.howToCelebrate, as.whatWorked, as.nextSteps]
            : [as.deeperOnTopic, as.howDoesItFeel, as.whatToChange];
        setSuggestions(fallbackSuggestions);
      }
    } catch (err) {
      setError(t.common?.error || "Error");
    } finally {
      setIsTyping(false);
    }
  };

  const handleSend = async (textOverride?: string) => {
    const text = textOverride || inputValue;
    if (!text.trim() && !selectedImage) return;

    trackAskSubmitted(text.length);
    setError(null);
    const userMsg: ExtendedChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      imageUrl: selectedImage || undefined,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    // Add user message first
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInputValue('');
    const currentImg = selectedImage;
    setSelectedImage(null);
    setIsTyping(true);
    setSuggestions([]);
    
    saveChatMessage({ id: userMsg.id, role: 'user', content: text, imageUrl: userMsg.imageUrl });

    try {
      // Build conversation history from current messages (including new user message)
      const conversationHistory = updatedMessages.map(m => ({
        role: m.role,
        content: m.content
      }));

      // Use context-aware response with CACHED session context for efficiency
      // Session context is loaded once at chat start, only history updates per message
      const result = await getContextAwareResponse(
        text,
        context,
        entries,
        decisions,
        patterns,
        conversationHistory,
        sessionContext, // Pass cached context for efficiency
        feedbackContext || undefined
      );
      
      // Use directActions from structured response, fallback to parsing from text
      const { cleanContent, actions: parsedActions, newSuggestions } = parseResponse(result.response);
      
      // Skip saving/showing empty responses
      if (!cleanContent || cleanContent.trim().length === 0) {
        console.warn("Empty chat response detected, not saving message");
        setError(t.common?.error || "Could not generate response. Please try again.");
        return;
      }
      
      const directActions = (result as any).directActions || [];
      const finalActions = directActions.length > 0 ? directActions : parsedActions.map((a: any) => ({ ...a, reason: '' }));

      const msgId = (Date.now() + 1).toString();
      const entities = (result as any).extractedEntities || { people: [], places: [], decisions: [] };
      const botMsg: ExtendedChatMessage = {
        id: msgId,
        role: 'model',
        content: cleanContent,
        contextLinks: finalActions.map((a: any) => `${a.type}|${a.title}`),
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        citedEntries: result.citedEntries,
        patternsUsed: result.patternsUsed,
        valuesUsed: result.valuesUsed,
        goalsUsed: result.goalsUsed,
        peopleUsed: entities.people || [],
        placesUsed: entities.places || [],
        decisionsUsed: entities.decisions || [],
        emotionalMode: result.emotionalMode,
        directActions: finalActions,
        responseType: (result as any).responseType || 'support',
        showSuggestions: (result as any).showSuggestions !== false
      };

      setMessages(prev => [...prev, botMsg]);
      setLastMessageId(msgId);
      
      saveChatMessage({ id: botMsg.id, role: 'model', content: botMsg.content, contextLinks: botMsg.contextLinks });
      
      if (!discoveryState.chatDiscovered) {
        completeStep('chatDiscovered');
      }
      
      // Set suggestions from AI response for conversation continuation
      if (result.suggestions && result.suggestions.length > 0) {
        setSuggestions(result.suggestions);
      } else if (newSuggestions.length > 0) {
        setSuggestions(newSuggestions);
      } else {
        // Generate contextual follow-up suggestions based on emotional mode
        const as = t.askSuggestions;
        const fallbackSuggestions = result.emotionalMode === 'struggle'
          ? [as.whatWouldHelp, as.tellMeMore, as.whatBlocksYou]
          : result.emotionalMode === 'celebration'
            ? [as.howToCelebrate, as.whatWorked, as.nextSteps]
            : [as.deeperOnTopic, as.howDoesItFeel, as.whatToChange];
        setSuggestions(fallbackSuggestions);
      }
    } catch (err) {
      setError(t.common?.error || "Error");
    } finally {
      setIsTyping(false);
    }
  };

  const executeAction = async (type: string, title: string, msgId: string, imageUrl?: string) => {
    const actionKey = `${msgId}-${title}`;
    if (justTracked.includes(actionKey)) return;

    // Optimistic update - show feedback immediately
    setJustTracked(prev => {
      const updated = [...prev, actionKey];
      localStorage.setItem('egovex_trackedActions', JSON.stringify(updated));
      return updated;
    });
    const toastMessages: Record<string, string> = {
      'TRACK': t.askPage.momentSaved,
      'DECIDE': t.askPage.decisionCreated,
      'VALUE': t.askPage.valueSaved,
      'GOAL': t.askPage.goalSaved
    };
    setToastMessage(toastMessages[type] || t.askPage.momentSaved);

    // Do the actual work in background
    try {
      if (type === 'TRACK') {
        await onNewEntry(title, imageUrl);
        if (!discoveryState.chatTrackingDiscovered) {
          completeStep('chatTrackingDiscovered');
          setTimeout(() => onNavigate?.('decisions'), 500);
        }
      } else if (type === 'DECIDE') {
        const result = await mapDecision(title, context, entries, patterns, decisions, feedbackContext || undefined);
        const decision: Decision = {
          id: Math.random().toString(36).substr(2, 9),
          context: "Dalla chat",
          ...result,
          title: title,
          status: 'pending',
          timestamp: Date.now()
        };
        setDecisions(prev => [decision, ...prev]);
        onDecisionCreated?.(decision);
      } else if (type === 'VALUE') {
        if (onAddValue) {
          onAddValue(title);
        }
      } else if (type === 'GOAL') {
        if (onAddGoal) {
          onAddGoal(title);
        }
      }
    } catch (err) {
      console.error('Action execution failed:', err);
      // Rollback on error
      setJustTracked(prev => {
        const updated = prev.filter(key => key !== actionKey);
        localStorage.setItem('egovex_trackedActions', JSON.stringify(updated));
        return updated;
      });
      setToastMessage(t.askPage.saveError || 'Errore nel salvataggio');
    }
  };

  return (
    <div className="h-full flex flex-col bg-white overflow-hidden relative lg:bg-[#F5F5F7]">
      {/* Apple-style Desktop Header */}
      <header className="shrink-0 bg-white/90 backdrop-blur-2xl border-b border-black/[0.04] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 lg:px-12 xl:px-16">
          {/* Main header row */}
          <div className="flex items-center justify-between h-14 lg:h-20">
            <h1 className="text-[30px] lg:text-4xl xl:text-[42px] font-black tracking-tighter leading-none">{t.navAsk}</h1>

            {/* Right side: Desktop context info + AI Personality Button */}
            <div className="flex items-center gap-3 lg:gap-4">
              {/* Stats badges - Desktop only */}
              <div className="hidden lg:flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-3 py-2 bg-indigo-50/80 rounded-xl">
                  <Brain size={14} className="text-indigo-500" />
                  <span className="text-[11px] font-bold text-indigo-600">{entries.length}</span>
                </div>
                {decisions.length > 0 && (
                  <div className="flex items-center gap-1.5 px-3 py-2 bg-purple-50/80 rounded-xl">
                    <Compass size={14} className="text-purple-500" />
                    <span className="text-[11px] font-bold text-purple-600">{decisions.length}</span>
                  </div>
                )}
                {patterns.length > 0 && (
                  <div className="flex items-center gap-1.5 px-3 py-2 bg-amber-50/80 rounded-xl">
                    <TrendingUp size={14} className="text-amber-500" />
                    <span className="text-[11px] font-bold text-amber-600">{patterns.length}</span>
                  </div>
                )}
              </div>

              {/* Progress indicator - Desktop only - TEMPORARILY DISABLED
              <div className="hidden lg:flex items-center gap-2 px-3 lg:px-4 py-2 lg:py-2.5 bg-gray-100/80 rounded-xl lg:rounded-2xl">
                <div className="w-16 lg:w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-500"
                    style={{ width: `${contextQuality.score}%` }}
                  />
                </div>
                <span className="text-[10px] lg:text-[11px] font-bold text-black/40">{contextQuality.score}%</span>
              </div>
              */}

              {/* Expand button - Desktop only */}
              {(context.values?.length > 0 || context.goals?.length > 0) && (
                <button
                  onClick={() => setHeaderExpanded(!headerExpanded)}
                  className="hidden lg:flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold text-black/30 hover:text-black/50 hover:bg-gray-100 rounded-xl transition-all"
                >
                  {headerExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
              )}

              {/* AI Personality Button - Always visible, rightmost */}
              <PersonalityHeaderButton
                personalityKey={currentPersonalityKey}
                language={context.language}
                onClick={() => setIsPersonalityModalOpen(true)}
              />
            </div>
          </div>
          
          {/* Expandable context section */}
          {headerExpanded && (context.values?.length > 0 || context.goals?.length > 0) && (
            <div className="pb-4 pt-2 space-y-3 animate-in slide-in-from-top-2 duration-200">
              {context.values?.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] lg:text-[11px] font-bold text-black/20 uppercase tracking-wider shrink-0">{t.chatCard.values}:</span>
                  {context.values.map((v, i) => (
                    <span key={i} className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-[11px] lg:text-xs font-medium">
                      {v}
                    </span>
                  ))}
                </div>
              )}
              {context.goals?.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] lg:text-[11px] font-bold text-black/20 uppercase tracking-wider shrink-0">{t.chatCard.goals}:</span>
                  {context.goals.map((g, i) => (
                    <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-[11px] lg:text-xs font-medium">
                      {g}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      <div ref={scrollContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto px-4 lg:px-16 xl:px-24 2xl:px-32 pt-6 lg:pt-12 pb-44 lg:pb-40 space-y-6 lg:space-y-8 no-scrollbar bg-[#F5F5F7] scroll-smooth">
        <div className="max-w-3xl lg:max-w-4xl xl:max-w-5xl mx-auto space-y-6 lg:space-y-8">
        {error && (
          <div className="flex items-center gap-3 p-4 bg-rose-50 border border-rose-100 rounded-2xl text-rose-600 text-sm font-bold animate-in slide-in-from-top-4">
            <AlertCircle size={18} /> {error}
          </div>
        )}

        {messages.length === 0 && (
          <ChatWelcome
            userName={context.userName}
            valuesCount={context.values?.length || 0}
            goalsCount={context.goals?.length || 0}
            entriesCount={entries.length}
            suggestions={suggestions}
            onSuggestionClick={(s) => handleSuggestionClick(s)}
            translations={t}
          />
        )}
        
        {(() => {
          let aiMessageIndex = 0;
          return messages.map((msg, idx) => {
          const significantCheck = msg.role === 'user' ? detectSignificantContent(msg.content) : { isSignificant: false, type: null };
          const isQuickTracked = quickTrackedMsgs.includes(msg.id);
          const currentAiIndex = msg.role === 'model' ? aiMessageIndex++ : -1;
          
          return msg.role === 'user' ? (
            <div key={msg.id} className="flex flex-col items-end animate-in slide-in-from-right-4 fade-in duration-500">
              <div className="max-w-[85%] lg:max-w-[60%] xl:max-w-[55%] px-5 lg:px-7 xl:px-8 py-4 lg:py-5 xl:py-6 rounded-[28px] lg:rounded-[32px] xl:rounded-[36px] bg-[#1D1D1F] text-white rounded-br-lg shadow-lg lg:shadow-xl transition-all">
                {msg.imageUrl && (
                  <div className="mb-3 lg:mb-4 rounded-xl lg:rounded-2xl overflow-hidden border border-white/10">
                    <img src={msg.imageUrl} className="w-full h-auto object-cover max-h-48 lg:max-h-60" alt="Allegato" />
                  </div>
                )}
                <div className="text-[15px] lg:text-[16px] xl:text-[17px] leading-relaxed lg:leading-[1.7] xl:leading-[1.8] whitespace-pre-wrap">
                  {msg.content}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2 lg:mt-3 px-2 lg:px-3">
                {significantCheck.isSignificant && (
                  <button
                    onClick={() => handleQuickTrack(msg.id, msg.content, msg.imageUrl)}
                    disabled={isQuickTracked}
                    className={`flex items-center gap-1.5 px-3 lg:px-4 py-1.5 lg:py-2 rounded-full text-[11px] lg:text-xs font-bold transition-all active:scale-95 ${
                      isQuickTracked 
                        ? 'bg-emerald-100 text-emerald-600 animate-pulse ring-2 ring-emerald-300 ring-offset-1' 
                        : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200 hover:shadow-md'
                    }`}
                  >
                    {isQuickTracked ? (
                      <>
                        <CheckCircle size={12} className="lg:w-3.5 lg:h-3.5" />
                        <span>{t.chatCard.saved}</span>
                      </>
                    ) : (
                      <>
                        <PlusCircle size={12} className="lg:w-3.5 lg:h-3.5" />
                        <span>{t.chatCard.remember}</span>
                      </>
                    )}
                  </button>
                )}
                <span className="text-[10px] lg:text-[11px] font-bold text-black/15 uppercase tracking-widest">{msg.timestamp}</span>
              </div>
            </div>
          ) : (
            <ChatCard
              key={msg.id}
              content={msg.content}
              timestamp={msg.timestamp}
              citedEntries={(msg as ExtendedChatMessage).citedEntries}
              patternsUsed={(msg as ExtendedChatMessage).patternsUsed}
              valuesUsed={(msg as ExtendedChatMessage).valuesUsed}
              goalsUsed={(msg as ExtendedChatMessage).goalsUsed}
              peopleUsed={(msg as ExtendedChatMessage).peopleUsed}
              placesUsed={(msg as ExtendedChatMessage).placesUsed}
              decisionsUsed={(msg as ExtendedChatMessage).decisionsUsed}
              emotionalMode={(msg as ExtendedChatMessage).emotionalMode}
              responseType={(msg as ExtendedChatMessage).responseType}
              actions={(msg as ExtendedChatMessage).directActions?.map(a => ({
                type: a.type,
                title: a.title,
                reason: a.reason
              })) || msg.contextLinks?.map(link => {
                const [type, title] = link.split('|');
                return { type: type as 'TRACK' | 'DECIDE' | 'VALUE' | 'GOAL', title, reason: '' };
              }) || []}
              isExecuted={(title) => justTracked.includes(`${msg.id}-${title}`)}
              onAction={(type, title) => executeAction(type, title, msg.id, msg.imageUrl)}
              isGreeting={idx === 0 && messages.length >= 1}
              enableTyping={msg.id === lastMessageId}
              messageIndex={currentAiIndex}
              translations={t.chatCard}
            />
          );
        });
        })()}

        {isTyping && (
          <div className="flex gap-1.5 px-6 py-4 bg-white border border-black/[0.04] rounded-full w-fit animate-pulse shadow-sm">
            <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce"></div>
            <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce [animation-delay:0.2s]"></div>
            <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce [animation-delay:0.4s]"></div>
          </div>
        )}

        {/* Suggestions - show before messages AND after AI responses */}
        {!isTyping && suggestions.length > 0 && messages.length > 0 && (
           <div className="pt-2 lg:pt-8 animate-in fade-in-0 slide-in-from-bottom-2 duration-500">
              <p className="text-[9px] lg:text-xs font-bold text-black/20 uppercase tracking-widest mb-1.5 lg:mb-4">{t.chatCard.continueWith}</p>
              <div className="flex flex-wrap gap-1.5 lg:gap-3">
                {suggestions.slice(0, 3).map((s, i) => (
                  <div 
                    key={i} 
                    onClick={() => handleSuggestionClick(s)} 
                    className="px-3 lg:px-6 py-1.5 lg:py-3 rounded-full text-[12px] lg:text-[15px] cursor-pointer transition-all duration-300 bg-white border border-black/[0.06] text-black/50 hover:text-indigo-600 hover:border-indigo-300 hover:bg-white hover:shadow-lg active:scale-95"
                  >
                    {s}
                  </div>
                ))}
              </div>
           </div>
        )}
        </div>
      </div>

      {/* Apple-style centered input bar */}
      <footer className="fixed left-0 right-0 z-50 p-3 lg:p-4 lg:left-1/2 lg:-translate-x-1/2 lg:w-full lg:max-w-3xl xl:max-w-4xl bottom-[calc(56px+env(safe-area-inset-bottom,16px))]">
        {selectedImage && (
          <div className="absolute bottom-full mb-4 left-4 lg:left-0 animate-in slide-in-from-bottom-4 duration-300">
            <div className="relative w-20 h-20 lg:w-28 lg:h-28 rounded-2xl lg:rounded-3xl overflow-hidden border-2 border-white shadow-2xl">
              <img src={selectedImage} className="w-full h-full object-cover" alt="Preview" />
              <button onClick={() => setSelectedImage(null)} className="absolute top-1.5 right-1.5 lg:top-2 lg:right-2 w-5 h-5 lg:w-6 lg:h-6 bg-black/60 text-white rounded-full flex items-center justify-center backdrop-blur-md hover:bg-black/80 transition-colors">
                <X size={10} className="lg:w-3 lg:h-3" />
              </button>
            </div>
          </div>
        )}
        <div className="bg-white/98 backdrop-blur-3xl rounded-2xl lg:rounded-[28px] xl:rounded-[32px] p-2 lg:p-3 border border-black/[0.06] shadow-[0_8px_40px_rgba(0,0,0,0.12)] lg:shadow-[0_16px_64px_rgba(0,0,0,0.14)] flex items-center gap-1 lg:gap-2 transition-shadow hover:shadow-[0_20px_60px_rgba(0,0,0,0.18)]">
          <button 
            onClick={() => fileInputRef.current?.click()} 
            className={`w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center transition-all rounded-xl lg:rounded-2xl hover:bg-gray-100 ${selectedImage ? 'text-indigo-600' : 'text-black/25 hover:text-black/50'}`}
          >
            <ImageIcon size={20} className="lg:w-6 lg:h-6" />
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={async (e) => {
              const file = e.target.files?.[0];
              if (file) {
                try {
                  // Compress image before storing (max 1200px, 80% quality)
                  const compressedImage = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      const img = new Image();
                      img.onload = () => {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        if (!ctx) { reject(new Error('No canvas context')); return; }
                        const maxSize = 1200;
                        let { width, height } = img;
                        if (width > height && width > maxSize) {
                          height = (height / width) * maxSize;
                          width = maxSize;
                        } else if (height > maxSize) {
                          width = (width / height) * maxSize;
                          height = maxSize;
                        }
                        canvas.width = width;
                        canvas.height = height;
                        ctx.drawImage(img, 0, 0, width, height);
                        resolve(canvas.toDataURL('image/jpeg', 0.8));
                      };
                      img.onerror = () => reject(new Error('Failed to load image'));
                      img.src = ev.target?.result as string;
                    };
                    reader.onerror = () => reject(new Error('Failed to read file'));
                    reader.readAsDataURL(file);
                  });
                  setSelectedImage(compressedImage);
                } catch (err) {
                  console.error('Failed to compress image:', err);
                  // Fallback to original
                  const reader = new FileReader();
                  reader.onloadend = () => setSelectedImage(reader.result as string);
                  reader.readAsDataURL(file);
                }
              }
          }} />
          <input 
            ref={chatInputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder={t.askPage.writeOrAskAdvice}
            className="flex-1 px-2 lg:px-4 py-2.5 lg:py-3.5 font-semibold outline-none text-[#1D1D1F] placeholder:text-black/20 text-[16px] lg:text-[17px] xl:text-[18px] bg-transparent"
          />
          <button 
            onClick={() => handleSend()} 
            disabled={!inputValue.trim() && !selectedImage}
            className={`w-10 h-10 lg:w-12 lg:h-12 rounded-xl lg:rounded-2xl flex items-center justify-center transition-all duration-300 ${inputValue.trim() || selectedImage ? 'bg-[#1D1D1F] text-white shadow-lg shadow-black/20 hover:bg-black hover:scale-105' : 'bg-black/5 text-black/15'}`}
          >
            <Send size={16} className="lg:w-5 lg:h-5" />
          </button>
        </div>
      </footer>

      <Toast
        message={toastMessage || ''}
        isVisible={!!toastMessage}
        onClose={() => setToastMessage(null)}
        type="success"
      />

      {/* Feature Discovery Overlay */}
      {currentDiscoveryStep && (
        <FeatureDiscovery
          featureKey={currentDiscoveryStep}
          discoveryState={discoveryState}
          onDismiss={completeStep}
          onActivate={handleDiscoveryActivate}
          onNavigate={onNavigate}
          currentView="consult"
          language={context.language}
          position="bottom"
        />
      )}

      {/* AI Personality Selector Modal */}
      <PersonalitySelectorModal
        isOpen={isPersonalityModalOpen}
        onClose={() => setIsPersonalityModalOpen(false)}
        currentPersonalityKey={currentPersonalityKey}
        onSelect={onPersonalityChange!}
        language={context.language}
      />
    </div>
  );
};

export default AskView;